import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';

const FranchiseForm: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contactNo: '',
    city: '',
    taluka: '',
    district: '',
    hasShop: '',
    shopOwnershipStatus: '',
    shopAddress: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'hasShop' && value === 'no') {
        updated.shopOwnershipStatus = '';
        updated.shopAddress = '';
      }
      return updated;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    if (!formData.hasShop) {
      setErrors({ hasShop: t('field_required') || 'Please select an option' });
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        fullName: formData.fullName,
        email: formData.email,
        contactNo: formData.contactNo,
        city: formData.city,
        taluka: formData.taluka,
        district: formData.district,
        hasShop: formData.hasShop === 'yes',
        shopOnRent: formData.shopOwnershipStatus === 'rented',
        shopAddress: formData.hasShop === 'yes' ? formData.shopAddress : '',
        message: formData.message,
      };

      if (process.env.NODE_ENV !== 'production') console.log('📤 Submitting franchise application:', submitData);

      // EmailJS configuration
      const serviceId = 'service_fsojiud';
      const templateId = 'template_t2tbfb9'; // TODO: Replace with your actual Franchise Template ID
      const publicKey = 'uTLjmEPZsOW67O6OZ'; // TODO: Replace with your actual Public Key

      const templateParams = {
        to_name: 'Owner',
        from_name: submitData.fullName,
        from_email: submitData.email,
        contact_no: submitData.contactNo,
        city: submitData.city,
        taluka: submitData.taluka,
        district: submitData.district,
        has_shop: submitData.hasShop ? 'Yes' : 'No',
        shop_on_rent: submitData.shopOnRent ? 'Yes' : 'No',
        shop_address: submitData.shopAddress,
        message: submitData.message,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      if (process.env.NODE_ENV !== 'production') console.log('✅ Franchise application submitted successfully');
      setSubmitted(true);
      setFormData({
        fullName: '', email: '', contactNo: '', city: '', taluka: '',
        district: '', hasShop: '', shopOwnershipStatus: '', shopAddress: '', message: ''
      });
      setTimeout(() => setSubmitted(false), 5000);

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('❌ Error submitting franchise application:', error);
      setErrors({ general: 'Failed to send the application. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="page-section franchise-form-standalone">
      <div className="container">
        <div className="franchise-form-section">
          <h3>{t('apply_franchise_title')}</h3>
          <p>{t('apply_franchise_subtitle')}</p>

          {submitted && (
            <div className="success-message">
              <p>✅ {t('application_success')}</p>
            </div>
          )}

          {errors.general && (
            <div className="error-message">
              <p>❌ {errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="franchise-form">
            <div className="form-group">
              <label htmlFor="franchise-fullName">{t('full_name_label')} *</label>
              <input type="text" id="franchise-fullName" name="fullName" value={formData.fullName} onChange={handleChange} required />
              {errors.fullName && <span className="error-text">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="franchise-email">{t('email_address_label')} *</label>
              <input type="email" id="franchise-email" name="email" value={formData.email} onChange={handleChange} required />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="franchise-contactNo">{t('contact_number_label')} *</label>
              <input type="tel" id="franchise-contactNo" name="contactNo" value={formData.contactNo} onChange={handleChange} required />
              {errors.contactNo && <span className="error-text">{errors.contactNo}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="franchise-city">{t('city')}</label>
              <input type="text" id="franchise-city" name="city" value={formData.city} onChange={handleChange} />
              {errors.city && <span className="error-text">{errors.city}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="franchise-taluka">{t('taluka')}</label>
              <input type="text" id="franchise-taluka" name="taluka" value={formData.taluka} onChange={handleChange} />
              {errors.taluka && <span className="error-text">{errors.taluka}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="franchise-district">{t('district')}</label>
              <input type="text" id="franchise-district" name="district" value={formData.district} onChange={handleChange} />
              {errors.district && <span className="error-text">{errors.district}</span>}
            </div>

            <div className="form-group radio-group">
              <label>{t('do_you_have_a_shop')} *</label>
              <div className="radio-options">
                <div className="radio-option">
                  <input type="radio" id="franchise-hasShop-yes" name="hasShop" value="yes" checked={formData.hasShop === 'yes'} onChange={handleChange} />
                  <label htmlFor="franchise-hasShop-yes" className="radio-label">{t('radio_yes') || 'Yes'}</label>
                </div>
                <div className="radio-option">
                  <input type="radio" id="franchise-hasShop-no" name="hasShop" value="no" checked={formData.hasShop === 'no'} onChange={handleChange} />
                  <label htmlFor="franchise-hasShop-no" className="radio-label">{t('radio_no') || 'No'}</label>
                </div>
              </div>
              {errors.hasShop && <span className="error-text">{errors.hasShop}</span>}
            </div>

            {formData.hasShop === 'yes' && (
              <>
                <div className="form-group radio-group">
                  <label>{t('is_the_shop_on_rent') || 'Is the shop rented or owned?'} *</label>
                  <div className="radio-options">
                    <div className="radio-option">
                      <input type="radio" id="franchise-shopOwnership-rented" name="shopOwnershipStatus" value="rented" checked={formData.shopOwnershipStatus === 'rented'} onChange={handleChange} required />
                      <label htmlFor="franchise-shopOwnership-rented" className="radio-label">{t('shop_rented') || 'Rented'}</label>
                    </div>
                    <div className="radio-option">
                      <input type="radio" id="franchise-shopOwnership-owned" name="shopOwnershipStatus" value="owned" checked={formData.shopOwnershipStatus === 'owned'} onChange={handleChange} required />
                      <label htmlFor="franchise-shopOwnership-owned" className="radio-label">{t('shop_owned') || 'Owned'}</label>
                    </div>
                  </div>
                  {errors.shopOwnershipStatus && <span className="error-text">{errors.shopOwnershipStatus}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="franchise-shopAddress">{t('shop_address')}</label>
                  <textarea id="franchise-shopAddress" name="shopAddress" value={formData.shopAddress} onChange={handleChange} rows={3} placeholder={t('enter_shop_address')} />
                  {errors.shopAddress && <span className="error-text">{errors.shopAddress}</span>}
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="franchise-message">{t('your_message_to_us')}</label>
              <textarea id="franchise-message" name="message" value={formData.message} onChange={handleChange} rows={4} placeholder={t('enterMessage')} />
              {errors.message && <span className="error-text">{errors.message}</span>}
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('submitting') : t('submit_application')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FranchiseForm;
